import * as React from 'react';
import './app.css';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import Routing from './Routing.js';
import Sidebar from './components/sidebar/Sidebar';
import Topbar from './components/topbar/Topbar';
import Login from './pages/login/Login.jsx';
import { Navigate, useLocation } from 'react-router-dom';
import { MenuOpen } from '@mui/icons-material';
import { Toaster } from 'react-hot-toast';
import useAutoLogout from './hooks/useAutoLogout';
import AutoLogoutWarning from './components/security/AutoLogoutWarning';
import { useTheme } from './context/ThemeContext';
import { SECURITY_CONFIG } from './config/security.config';

const drawerWidth = 240;

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: `-${drawerWidth}px`,
    ...(open && {
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: 0,
    }),
  }),
);


const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  boxShadow: theme.palette.mode === 'dark' 
    ? '0 1px 3px 0 rgba(0, 0, 0, 0.3)' 
    : '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(27, 31, 35, 0.1)',
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));





export default function App() {
  const router = useLocation();
  const [open, setOpen] = React.useState(true);
  const auth = localStorage.getItem("adminToken")
  const { enabled, timeout, warningTime } = SECURITY_CONFIG.autoLogout;
  
  // Auto-logout security feature
  // Only enable when user is authenticated (not on login page)
  const { showWarning, countdown, extendSession, logout } = useAutoLogout({
    timeout,
    warningTime,
    enabled: enabled && auth && router.pathname !== '/'  // Only enable when logged in
  });
  
  const handleDrawerOpen = () => {
    setOpen(true);
  };
  const handleDrawerClose = () => {
    setOpen(false);
  };
  const handleResize = () => {
    if (window.innerWidth < 1020) {
      setOpen(false);
    } else {
      setOpen(true);
    }
  }
  React.useEffect(() => {
    window.addEventListener("resize", handleResize);
    // Cleanup to prevent memory leak
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []); // Empty dependency array - only run on mount/unmount
  return (
    <>
      <Toaster
        toastOptions={{
          className: '',
          style: {
            padding: '16px',
            color: '#713200',
            width:"100%",
            fontFamily:"system-ui",
            fontSize:"14px",
            fontWeight:"500"
          },
        }}
      />
      
      {/* Auto-logout warning dialog */}
      <AutoLogoutWarning
        open={showWarning}
        countdown={countdown}
        onStayLoggedIn={extendSession}
        onLogout={logout}
      />
      
      {router.pathname !== "/"
        ?
        <Box sx={{
          display: 'flex',
          height: '100%',
          bgcolor: 'background.default',
          minHeight: '100vh',
        }}>
          <AppBar className="PanelTopBar" position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
            <Toolbar>
              {open === false
                ?
                <IconButton
                  aria-label="open drawer"
                  onClick={handleDrawerOpen}
                  edge="start"
                  sx={{ mr: 2, color: "text.primary", ...(open && { display: 'none' }) }}
                >
                  <MenuIcon className='topBarIcon' />
                </IconButton>
                :
                <IconButton
                  onClick={handleDrawerClose}
                  edge="start"
                  sx={{ mr: 2, color: "text.primary" }}
                >
                  <MenuOpen className='topBarIcon' />
                </IconButton>
              }
              <Topbar />
            </Toolbar>
          </AppBar>

          <Drawer
            sx={{
              width: drawerWidth,
              flexShrink: 0,
              '& .MuiDrawer-paper': {
                width: drawerWidth,
                boxSizing: 'border-box',
                bgcolor: 'background.sidebar',
              },

            }}
            variant="persistent"
            anchor="left"
            open={open}
          >
            <Divider />
            <IconButton onClick={handleDrawerClose}>
              <MenuOpen sx={{ position: 'left' }} />
            </IconButton>
            <Sidebar />
          </Drawer>

          <Main open={open}>
            <Toolbar/>
            <Routing />
          </Main>
        </Box>
        : router.pathname === "/" && auth
          ?
          <Navigate to="/dashboard"></Navigate>
          :
          <Login />
      }
    </>
  )
}

